import { useParams, Link } from "react-router-dom";
import { FiExternalLink, FiArrowLeft } from "react-icons/fi";
import { Spinner, Badge } from "@/components/ui";
import { humanize, SECTION_LABELS } from "@/config/constants";
import { ROUTES } from "@/routes/paths";
import { useTrackOrder } from "../hooks/useTrackOrder";

const Row = ({ label, children }) =>
  children ? (
    <div className="flex flex-col gap-1 border-b border-border py-3 sm:flex-row sm:gap-4">
      <span className="w-40 shrink-0 text-sm text-muted-foreground">{label}</span>
      <span className="min-w-0 break-words text-foreground">{children}</span>
    </div>
  ) : null;

const addressOf = (o) =>
  [o.address_line1, o.address_line2, o.city, o.state, o.postal_code, o.customer_country]
    .filter(Boolean)
    .join(", ");

/**
 * Shows a placed order to whoever holds its private link.
 * The token in the URL is the credential, so a wrong or expired one must look
 * like a plain "not found" rather than hinting that some other order exists.
 */
export function TrackOrderPage({ custom = false }) {
  const { token } = useParams();
  const { data: order, isLoading, error } = useTrackOrder(token, { custom });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="mx-auto max-w-lg py-12 text-center">
        <h1 className="mb-2 text-xl font-bold text-foreground">Order not found</h1>
        <p className="mb-6 text-muted-foreground">
          This link is not valid. Please use the link from your confirmation email — it
          is the exact address we sent you.
        </p>
        <Link to={ROUTES.PRODUCTS} className="text-primary hover:underline">
          Browse products
        </Link>
      </div>
    );
  }

  const status = custom ? order.custom_order_status : order.order_status;
  const notifications = order.notifications ?? [];

  return (
    <div className="mx-auto max-w-2xl">
      <Link
        to={ROUTES.ORDERS}
        className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <FiArrowLeft /> My orders
      </Link>

      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm text-muted-foreground">Order</p>
          <h1 className="text-2xl font-bold text-foreground">{order.order_number}</h1>
        </div>
        <Badge>{humanize(status)}</Badge>
      </div>

      <div className="rounded-lg border border-border bg-card p-4 text-card-foreground">
        <Row label="Placed">{new Date(order.createdAt).toLocaleString()}</Row>
        {!custom && <Row label="Section">{SECTION_LABELS[order.section] ?? order.section}</Row>}
        {!custom && <Row label="Product">{order.product_title}</Row>}
        {!custom && order.product_url && (
          <Row label="Product link">
            <a
              href={order.product_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              {order.product_url} <FiExternalLink className="shrink-0" />
            </a>
          </Row>
        )}
        {custom && <Row label="Designer">{order.designer?.name}</Row>}
        {custom && <Row label="Event date">{order.event_date}</Row>}
        {custom && <Row label="Colour preference">{order.color_preference}</Row>}
        {custom && <Row label="Measurements">{order.measurements}</Row>}
        {custom && <Row label="Instructions">{order.special_instructions}</Row>}
        <Row label="Total">{order.total_amount}</Row>
        <Row label="Tracking number">{order.tracking_number}</Row>
        <Row label="Notes">{order.notes}</Row>
        <Row label="Name">{order.customer_name}</Row>
        <Row label="Email">{order.customer_email}</Row>
        <Row label="Phone">{order.customer_phone}</Row>
        <Row label="Deliver to">{addressOf(order)}</Row>
      </div>

      {order.quote && (
        <div className="mt-6 rounded-lg border border-border bg-card p-4 text-card-foreground">
          <h2 className="mb-2 font-semibold text-foreground">Quote</h2>
          <Row label="Amount">{order.quote.amount}</Row>
          <Row label="Status">{humanize(order.quote.quote_status)}</Row>
          <Row label="Valid until">{order.quote.valid_until}</Row>
        </div>
      )}

      {notifications.length > 0 && (
        <div className="mt-6 rounded-lg border border-border bg-card p-4 text-card-foreground">
          <h2 className="mb-3 font-semibold text-foreground">Updates</h2>
          <ul className="space-y-3">
            {notifications.map((n) => (
              <li key={n.documentId} className="border-b border-border pb-3 last:border-0 last:pb-0">
                <p className="font-medium text-foreground">{n.title}</p>
                <p className="text-sm text-muted-foreground">{n.message}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="mt-6 text-xs text-muted-foreground">
        Keep this page's link — it is how you get back to this order. Anyone with the
        link can see it, so please don't share it.
      </p>
    </div>
  );
}

export default TrackOrderPage;
